import React from "react";
import { Button, Input, Popover, Table, Tag, Tooltip } from "antd";
import { QuestionCircleOutlined, SearchOutlined } from "@ant-design/icons";

import {
  CATEGORIES_AFFICHAGE,
  COULEURS_CATEGORIES,
  COULEUR_KIWEE,
  aideCategories,
  estKiwee,
  filenameJob,
} from "./categoriesFlux";
import { categorieJob, filtresActifs, statutsJob, texteDernierBuild } from "./filtresMap";
import { DEFINITIONS_STATUTS } from "./MapEntete";

// Pastilles de la barre : 5 categories + Kiwee (filtre transverse, pas une categorie).
const PASTILLES = [
  ...CATEGORIES_AFFICHAGE.map((cat) => ({ cat, couleur: COULEURS_CATEGORIES[cat] })),
  { cat: "KIWEE", libelle: "KIWEE", couleur: COULEUR_KIWEE },
];

// Statuts a ne pas confondre, du plus fin (un traitement) au plus large (un serveur entier).
const AIDE_STATUTS = [
  ["grise", "Grisé"],
  ["sans", "Env. sans traitement"],
  ["serveurs", "Serveur non remonté"],
];

function AideCategories() {
  const aide = aideCategories();
  return (
    <div style={{ maxWidth: 440 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{aide.titre}</div>
      <ol style={{ paddingLeft: 18, marginBottom: 6 }}>
        {aide.regles.map((r) => (
          <li key={r.categorie}>
            <strong>{r.categorie}</strong> : {r.motifs.join(" ; ")}
            {r.remarque && (
              <div style={{ fontSize: 12, color: COULEUR_KIWEE }}>{r.remarque}</div>
            )}
          </li>
        ))}
      </ol>
      {aide.notes.map((n) => (
        <div key={n} style={{ fontSize: 12, color: "#716F81" }}>
          {n}
        </div>
      ))}
      <div style={{ fontWeight: 600, margin: "10px 0 6px" }}>Grisé, non remonté, sans traitement</div>
      <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
        {AIDE_STATUTS.map(([cle, libelle]) => (
          <li key={cle}>
            <strong>{libelle}</strong> : {DEFINITIONS_STATUTS[cle].long}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Barre de filtres : pastilles arrondies de categories (compteur + pastille rouge d'erreurs),
// pastille Kiwee, aide "?", recherche. Le filtre de statut est porte par les cartes d'en-tete.
export function BarreFiltresMap({
  filtres,
  qSaisie,
  onQSaisie,
  onChange,
  onReset,
  compteurs,
}) {
  const toggleCat = (cle) => {
    const cats = filtres.categories.includes(cle)
      ? filtres.categories.filter((c) => c !== cle)
      : [...filtres.categories, cle];
    onChange({ categories: cats });
  };
  const pastille = ({ cat, libelle, couleur }) => {
    const cle = cat.toLowerCase();
    const actif = filtres.categories.includes(cle);
    const c = (compteurs && compteurs[cat]) || { total: 0, erreurs: 0 };
    return (
      <button
        key={cat}
        type="button"
        role="checkbox"
        aria-checked={actif}
        className={`map-chip${actif ? " on" : ""}`}
        style={{ "--cc": couleur }}
        onClick={() => toggleCat(cle)}
        data-testid={`filtre-cat-${cle}`}
      >
        <span className="map-dot" style={{ background: couleur }} />
        {libelle || cat} <span className="map-chip-n">{c.total}</span>
        {c.erreurs ? (
          <Tooltip title={`${c.erreurs} en erreur`}>
            <span className="map-chip-e">{c.erreurs}</span>
          </Tooltip>
        ) : null}
      </button>
    );
  };
  return (
    <div className="map-bar map-filtres" role="group" aria-label="Catégories">
      {PASTILLES.slice(0, -1).map(pastille)}
      <span className="map-sep" />
      {pastille(PASTILLES[PASTILLES.length - 1])}
      <Popover
        trigger={["click", "focus"]}
        placement="bottomLeft"
        content={<AideCategories />}
      >
        <Button
          type="text"
          size="small"
          aria-label="Règles de catégorisation"
          icon={<QuestionCircleOutlined />}
        />
      </Popover>
      <Input
        allowClear
        className="map-search"
        prefix={<SearchOutlined />}
        placeholder="Groupe, environnement, traitement…"
        value={qSaisie}
        onChange={(e) => onQSaisie(e.target.value)}
        aria-label="Recherche"
      />
      {(filtresActifs(filtres) || qSaisie) && (
        <Button size="small" onClick={onReset}>
          Réinitialiser
        </Button>
      )}
    </div>
  );
}

export function MessageAucunResultat({ onReset, serveurs = false }) {
  return (
    <div style={{ textAlign: "center", marginTop: 48 }}>
      <p>
        {serveurs
          ? "Aucun serveur non remonté ne correspond aux filtres"
          : "Aucun traitement ne correspond aux filtres"}
      </p>
      <Button type="primary" onClick={onReset}>
        Réinitialiser
      </Button>
    </div>
  );
}

// Message d'un environnement non remonte (BI-682) : treemap au niveau env, panneau lateral.
export function MessageNonRemonte({ dernierBuild }) {
  return (
    <p data-testid="message-non-remonte">
      Aucun build depuis 24 h — {texteDernierBuild(dernierBuild, formaterDate)}
    </p>
  );
}

const LIBELLE_STATUT_JOB = {
  non_remonte: ["Non remonté", "purple"],
  ok: ["OK", "green"],
  warning: ["Warning", "orange"],
  erreur: ["Erreur", "red"],
  grise: ["Grisé", "default"],
  encours: ["En cours", "blue"],
  sans: ["Sans traitement", "default"],
};

// "2026-10-27 19:28:16+01" -> "27/10/2026 19:28" (heure de Paris) ; valeur illisible : brute.
export function formaterDate(v) {
  if (!v) return "";
  const iso = String(v)
    .trim()
    .replace(" ", "T")
    .replace(/([+-]\d{2})$/, "$1:00");
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(v);
  const p = new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(d);
  const g = (t) => (p.find((x) => x.type === t) || {}).value;
  return `${g("day")}/${g("month")}/${g("year")} ${g("hour")}:${g("minute")}`;
}

// "00:00:05.123456" -> "00:00:05" (hh:mm:ss, sans fraction).
export function formaterDuree(v) {
  if (!v) return "";
  return String(v).replace(/\.\d+$/, "");
}

const cmp = (a, b) => String(a || "").localeCompare(String(b || ""), "fr", { numeric: true });

// lignes : sortie de collecterJobs ; onRowClick(job).
// envsSans : environnements sans traitement (collecterEnvs), affiches en ligne grisee ;
// onEnvClick(env) ouvre alors leur panneau de detail.
export function TableauJobs({ lignes, onRowClick, envsSans = [], onEnvClick, envsNonRemontes = [] }) {
  const data = lignes.map(({ job, group, server, env }, i) => ({
    key: `${job.traitement_id}-${i}`,
    job,
    group,
    server,
    env,
    categorie: categorieJob(job),
    kiwee: estKiwee(filenameJob(job)),
    name: job.name,
    statut: statutsJob(job)[0] || "ok",
    start: job.start_date_time,
    duree: job.duration,
  }));
  envsSans.forEach((e, i) =>
    data.push({
      key: `sans-${e.key}-${i}`,
      envSans: e,
      group: e.group,
      server: e.server,
      env: e.env,
      categorie: "",
      kiwee: false,
      name: "Aucun traitement",
      statut: "sans",
      start: "",
      duree: "",
    })
  );
  envsNonRemontes.forEach((e, i) =>
    data.push({
      key: `nr-${e.key}-${i}`,
      envSans: e,
      envNr: true,
      group: e.group,
      server: e.server,
      env: e.env,
      categorie: "—",
      kiwee: false,
      name: "—",
      statut: "non_remonte",
      start: e.dernierBuild || "",
      duree: "",
    })
  );
  const columns = [
    { title: "Groupe", dataIndex: "group", sorter: (a, b) => cmp(a.group, b.group) },
    { title: "Serveur", dataIndex: "server", sorter: (a, b) => cmp(a.server, b.server) },
    { title: "Env", dataIndex: "env", sorter: (a, b) => cmp(a.env, b.env) },
    {
      title: "Catégorie",
      dataIndex: "categorie",
      sorter: (a, b) => cmp(a.categorie, b.categorie),
      render: (c, row) => (
        <span>
          {c} {row.kiwee && <Tag color="purple">KIWEE</Tag>}
        </span>
      ),
    },
    { title: "Traitement", dataIndex: "name", sorter: (a, b) => cmp(a.name, b.name) },
    {
      title: "Statut",
      dataIndex: "statut",
      sorter: (a, b) => cmp(a.statut, b.statut),
      render: (s, row) => {
        const [label, color] = LIBELLE_STATUT_JOB[s] || [s, "default"];
        return (
          <Tag color={color}>
            {label}
            {row.job && row.job.running && s !== "encours" ? " (en cours)" : ""}
          </Tag>
        );
      },
    },
    {
      title: "Dernier lancement",
      dataIndex: "start",
      sorter: (a, b) => cmp(a.start, b.start),
      render: (v, row) => (row.envNr && !v ? "—" : formaterDate(v)),
    },
    {
      title: "Durée",
      dataIndex: "duree",
      sorter: (a, b) => cmp(a.duree, b.duree),
      render: formaterDuree,
    },
  ];
  return (
    <Table
      size="small"
      columns={columns}
      dataSource={data}
      pagination={{ pageSize: 50, showSizeChanger: true }}
      scroll={{ y: "calc(100vh - 340px)" }}
      onRow={(row) => ({
        onClick: () => (row.envSans ? onEnvClick && onEnvClick(row.envSans) : onRowClick(row.job)),
        style: { cursor: "pointer" },
      })}
    />
  );
}
